import moment from 'moment';
import { mapEpisodeDetails } from './helpers';

const EPISODE_DETAILS_KEYS_TO_OMIT_FROM_EPISODES_DETAILS = ['Close the Gap'];

const shouldOmitDetailsKey = (key) => {
  if (!key) return false;
  return EPISODE_DETAILS_KEYS_TO_OMIT_FROM_EPISODES_DETAILS.some(
    (target) => String(target).toLowerCase() === String(key).toLowerCase(),
  );
};

const sanitizeEpisodeDetails = (details) => {
  if (Array.isArray(details)) {
    return details.filter((item) => !shouldOmitDetailsKey(item?.meta_name));
  }
  if (details && typeof details === 'object') {
    const next = { ...details };
    for (const key of Object.keys(next)) {
      if (shouldOmitDetailsKey(key)) {
        delete next[key];
      }
    }
    return next;
  }
  return details;
};

const extractDetailValue = (details, key) => {
  if (!details || !key) return undefined;
  if (Array.isArray(details)) {
    const found = details.find(
      (d) => String(d?.meta_name ?? '').trim() === String(key).trim(),
    );
    return found?.meta_value;
  }
  if (details && typeof details === 'object') {
    return details[key];
  }
  return undefined;
};

const buildEpisodeDetailsValues = (details) => {
  const base = mapEpisodeDetails(details);
  const lifted = {};
  for (const key of EPISODE_DETAILS_KEYS_TO_OMIT_FROM_EPISODES_DETAILS) {
    const v = extractDetailValue(details, key);
    if (v === null || v === undefined) continue;
    const s = String(v).trim();
    if (!s) continue;
    lifted[key] = s;
  }
  return { ...base, ...lifted };
};

const toTime = (value) => {
  if (!value) return 0;
  const t = Date.parse(String(value));
  return Number.isFinite(t) ? t : 0;
};

const getLatestEpisodeIndex = (episodes) => {
  if (!Array.isArray(episodes) || episodes.length === 0) return 0;
  const openEpisodes = episodes.filter(
    (ep) =>
      ep?.episode_closed === false && (ep?.episode_closed_date ?? null) == null,
  );
  const candidates = openEpisodes.length > 0 ? openEpisodes : episodes;

  let bestEpisode = candidates[0];
  let bestOpened = -1;
  let bestUpdated = -1;
  let bestIdScore = -1;

  for (const ep of candidates) {
    const opened = toTime(ep?.episode_opened_date);
    const updated = toTime(ep?.updated_at);
    const idScore =
      typeof ep?.id === 'number'
        ? ep.id
        : typeof ep?.id === 'string'
          ? Number(ep.id)
          : 0;

    if (
      opened > bestOpened ||
      (opened === bestOpened && updated > bestUpdated) ||
      (opened === bestOpened &&
        updated === bestUpdated &&
        idScore > bestIdScore)
    ) {
      bestEpisode = ep;
      bestOpened = opened;
      bestUpdated = updated;
      bestIdScore = idScore;
    }
  }

  const idx = episodes.indexOf(bestEpisode);
  return idx >= 0 ? idx : 0;
};

const DataMapperProfileBar = (
  update,
  data,
  access_token,
  selectedTenancy,
  updatedClinicMeta,
) => {
  let clinicsData = [];

  // comment out because we are moving to client_clinic_episode
  // function hpSync(cl) {
  //   let temp = Object.assign({}, JSON.parse(JSON.stringify(cl)));
  //   temp.healthProfessionals = [];
  //   if (cl.details && cl.details['Health Professionals']) {
  //     temp.healthProfessionals = cl.details['Health Professionals'];
  //   }
  //   return temp.healthProfessionals;
  // }
  for (let i = 0; i < data.clinics.length; i++) {
    const currentUpdatedEpisode =
      updatedClinicMeta.find(
        (meta) => meta.meta_name === data.clinics[i].meta_name,
      ) || data.clinics[i].episodes;
    const latestEpisodeIndex = getLatestEpisodeIndex(currentUpdatedEpisode);
    const latestEpisode =
      currentUpdatedEpisode && currentUpdatedEpisode.length > 0
        ? currentUpdatedEpisode[latestEpisodeIndex]
        : null;
    if (latestEpisode) {
      const sanitizedEpisodes = Array.isArray(currentUpdatedEpisode)
        ? currentUpdatedEpisode.map((ep) => ({
            ...ep,
            details: sanitizeEpisodeDetails(ep?.details),
          }))
        : currentUpdatedEpisode;
      const c = {
        clinicId: data.clinics[i].id,
        episodeNo: latestEpisode.episode_no ?? null,
        episodeClosed: latestEpisode.episode_closed ?? false,
        // healthProfessionals: hpSync(data.clinics[i]),
        healthProfessionals: latestEpisode.details?.healthProfessionals ?? [],
        primaryTeams: latestEpisode.primary_teams?.split(',') ?? [],
        episodes: sanitizedEpisodes,
        episodeDetailsValues: buildEpisodeDetailsValues(latestEpisode.details),
      };

      clinicsData.push(c);
    }
  }

  // Process user_episodes_details
  const processedEpisodes = {};
  // data.usr_episodes_details.forEach((episode) => {
  //   const description = episode.detail?.description;
  //   const value = episode.detail?.fhir?.valueQuantity;
  //   const date = moment(episode.event_date).format('DD-MM-YY');

  //   processedEpisodes[description] = { value, date };
  // });

  let response = {
    firstName: data.first_name,
    middleName: data.middle_name,
    lastName: data.last_name,
    dob: update.dob
      ? moment(update.dob).format('YYYY-MM-DD')
      : moment(data.dob).format('YYYY-MM-DD'),
    gender: update.gender ?? data.gender,
    email: update.email ?? data.contact_email,
    postcode: update.postcode ?? data.postcode ?? null,
    phone: data.phone,
    pcName: update.pcName ?? data.pcName,
    pcRelation: update.pcRelation ?? data.pcRelation,
    pcPhone: update.pcPhone ?? data.pcPhone,
    pcEmail: update.pcEmail ?? data.pcEmail,
    clinics: clinicsData,
    access_token: access_token,
    tenancyId: selectedTenancy.tenancy_id,
    ...processedEpisodes, // Add the processed episodes to the response
  };

  return response;
};

export default DataMapperProfileBar;
